/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { PrimaryButton } from '@fluentui/react/lib/Button';
import { Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logDemoButtonClick, logNextSteps } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { textAnalyticsPricingLink } from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';

type CallCenterNextStepsPricingProps = { isResultsEmpty: boolean };

const messages = defineMessages({
    description2: { id: 'languageStudio.callCenterNextStepsPricing.description2', defaultMessage: 'or use the' },
    pricingImageAlt: { id: 'languageStudio.callCenterNextStepsPricing.pricingImageAlt', defaultMessage: 'Pricing icon' },
    azurePricing: { id: 'languageStudio.callCenterNextStepsPricing.azurePricing', defaultMessage: `Azure pricing calculator` },
    speechPricing: { id: 'languageStudio.callCenterNextStepsPricing.speechPricing', defaultMessage: 'Azure Speech service pricing.' },
    talkToSpecialist: { id: 'languageStudio.callCenterNextStepsPricing.talkToSpecialist', defaultMessage: 'Talk to a sales specialist' },
    languagePricing: { id: 'languageStudio.callCenterNextStepsPricing.languagePricing', defaultMessage: 'Azure Language services pricing' },
    getLanguagePricing: {
        id: 'languageStudio.callCenterNextStepsPricing.getLanguagePricing',
        defaultMessage: 'To get Language pricing, please visit'
    },
    getSpeechPricing: {
        id: 'languageStudio.callCenterNextStepsPricing.getSpeechPricing',
        defaultMessage: 'To get pricing for speech to text, please visit'
    },
    requestPricing: {
        id: 'languageStudio.callCenterNextStepsPricing.requestPricing',
        defaultMessage: 'To request a pricing quote and get answers to your pricing questions:'
    }
});

export const CallCenterNextStepsPricing = ({ isResultsEmpty }: CallCenterNextStepsPricingProps) => {
    const { localize } = useLocalization();

    const handleClickLink = (linkName: string) => {
        logDemoButtonClick('Call center', 'Nextsteps:pricing', linkName);
        if (!isResultsEmpty) {
            logNextSteps();
        }
    };

    return (
        <Stack tokens={{ childrenGap: 25 }}>
            <Stack tokens={{ childrenGap: 8 }}>
                <LuisText>
                    {localize(messages.getSpeechPricing)}&nbsp;
                    <ExternalLink href="https://aka.ms/speech-pricing" onClick={() => handleClickLink('SpeechPricing')}>
                        {localize(messages.speechPricing)}
                    </ExternalLink>
                </LuisText>
                <LuisText>
                    {localize(messages.getLanguagePricing)}&nbsp;
                    <ExternalLink href={textAnalyticsPricingLink} onClick={() => handleClickLink('LanguagePricing')}>
                        {localize(messages.languagePricing)}
                    </ExternalLink>
                </LuisText>
                <LuisText>
                    {localize(messages.description2)}&nbsp;
                    <ExternalLink href="https://aka.ms/language-pricing-calculator" onClick={() => handleClickLink('AzurePricing')}>
                        {localize(messages.azurePricing)}
                    </ExternalLink>
                </LuisText>
            </Stack>

            <Image src={getAssetsUrl('assets/images/languageStudio/prebuiltDemos/pricing.svg')} alt={localize(messages.pricingImageAlt)} />

            <LuisText>{localize(messages.requestPricing)}</LuisText>

            <PrimaryButton
                target="_blank"
                text={localize(messages.talkToSpecialist)}
                href="https://aka.ms/language-contact-sales"
                styles={{ root: { alignSelf: 'flex-start' } }}
                onClick={() => handleClickLink('TalkToSpecialist')}
            />
        </Stack>
    );
};
